<!--
  One modal, used by the rename, move and delete dialogs.

  Three near-identical dialogs would have drifted in three directions; the
  differences between them are a title, a body and the word on the confirm
  button, so those are the props and everything else is shared: the backdrop,
  Escape to cancel, the focus trap's cheap cousin (autofocus on the first
  control), and the fact that a destructive confirm is red.

  It is a plain overlay rather than <dialog>: the page has one stacking context,
  nothing here needs the top layer, and `showModal()` would put the decision
  about when to open it in an effect instead of in the markup.
-->
<script lang="ts">
  import type { Snippet } from 'svelte'

  let {
    title,
    confirmLabel = 'Save',
    confirmDisabled = false,
    danger = false,
    busy = false,
    error = '',
    onconfirm,
    oncancel,
    children,
  }: {
    title: string
    confirmLabel?: string
    confirmDisabled?: boolean
    danger?: boolean
    busy?: boolean
    error?: string
    onconfirm: () => void
    oncancel: () => void
    children?: Snippet
  } = $props()

  function onkeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.stopPropagation()
      oncancel()
    }
  }

  function submit(event: Event) {
    event.preventDefault()
    if (confirmDisabled || busy) return
    onconfirm()
  }
</script>

<svelte:window on:keydown={onkeydown} />

<div class="backdrop">
  <!-- The backdrop is its own button, underneath the sheet rather than around
       it: clicking outside cancels, which is what a person expects, and a
       click inside never reaches it, so no event has to be stopped. -->
  <button type="button" class="backdrop-hit" aria-label="Cancel" onclick={oncancel}></button>
  <!-- The dialog role sits on the wrapper, not on the form: a <form> is not an
       interactive element and giving it an interactive role is an a11y lint
       error for a reason -- a screen reader is told about the form twice. -->
  <div class="sheet" role="dialog" aria-modal="true" aria-label={title}>
    <form class="sheet-form" onsubmit={submit}>
      <h2 class="sheet-title">{title}</h2>
      <div class="sheet-body">
        {@render children?.()}
      </div>
      {#if error}
        <p class="sheet-error" role="alert">{error}</p>
      {/if}
      <div class="sheet-actions">
        <button type="button" class="btn btn-quiet btn-small" onclick={oncancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="submit"
          class={`btn btn-small ${danger ? 'btn-danger' : 'btn-primary'}`}
          disabled={confirmDisabled || busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </form>
  </div>
</div>

<style>
  .backdrop {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: grid;
    place-items: center;
    padding: 1rem;
  }

  .backdrop-hit {
    position: absolute;
    inset: 0;
    border: 0;
    padding: 0;
    background: rgb(0 0 0 / 35%);
    cursor: default;
  }

  .sheet {
    position: relative;
    width: min(32rem, 100%);
    padding: 1.25rem;
    border: 1px solid var(--color-line, #211d1824);
    border-radius: 0.75rem;
    background: var(--color-paper, #faf9f5);
    box-shadow: 0 20px 50px rgb(0 0 0 / 22%);
  }

  .sheet-form {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .sheet-title {
    margin: 0;
    font-size: 1.15rem;
    font-family: var(--font-sans, ui-sans-serif, system-ui, sans-serif);
  }

  .sheet-body {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    font-size: var(--text-body, 1.0625rem);
  }

  .sheet-error {
    margin: 0;
    color: var(--color-red-deep, #c01030);
    font-size: var(--text-meta, 0.8125rem);
  }

  .sheet-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
</style>
